import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { useIsSessionUrgencyEnabled } from '@/features/flags';
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { getSessionsWithPagination } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { useTranslations } from '@heidi/runtime/i18n';
import {
  GET_INFINITE_SESSIONS_WITH_PAGINATION,
  SESSIONS_PAGE_SIZE,
  SESSIONS_QUERY_RETRY_DELAY,
  type SessionSearchSortOrder,
  type SessionSortBy,
} from '@heidi/session/contracts';
import {
  isUrgentOnlyFilterAtom,
  selectedDateRangeFilterAtom,
  selectedPatientProfilesFilterAtom,
  selectedStatusesFilterAtom,
  SESSIONS_QUERY_RETRY,
  SESSIONS_REFETCH_INTERVAL,
  sessionsSortByAtom,
  sessionsSortOrderAtom,
  useDebouncedSessionSearchValue,
  type SortOption,
  type SortOrder,
} from '@heidi/session/data';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { useQueryUser } from './useQueryUser';

// Query key for cache invalidation

// Map frontend sort option to backend sort field. Partial because `urgency`
// is a frontend-only flag with no backend sort field — it falls back to the
// default `created_at` order here and is sorted client-side over loaded rows.
const sortByMapping: Partial<Record<SortOption, SessionSortBy>> = {
  createdAt: 'created_at',
  updatedAt: 'updated_at',
  name: 'name',
  status: 'status',
};

// Map frontend sort order to backend sort order
const mapSortOrderToBackend = (sortOrder: SortOrder): SessionSearchSortOrder => {
  return sortOrder === 'asc' ? 'ASC' : 'DESC';
};

export function useQueryAdminSessions() {
  const { t } = useTranslations();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const pageSize = SESSIONS_PAGE_SIZE;
  const { isAuthenticated } = useIsAuthenticated();
  const { data: user } = useQueryUser((data) => data?.kinde_user_id);
  const isDesktopOnlineReady = useIsDesktopOnlineReady();

  // Get filter values from atoms
  const selectedStatuses = useAtomValue(selectedStatusesFilterAtom);
  const selectedDateRange = useAtomValue(selectedDateRangeFilterAtom);
  const selectedPatientProfiles = useAtomValue(selectedPatientProfilesFilterAtom);
  const searchValue = useDebouncedSessionSearchValue();

  // Get untitled session text for backend search matching
  const untitledText = t('shared.UntitledSession');

  // Get sort values from atoms
  const sortBy = useAtomValue(sessionsSortByAtom);
  const sortOrder = useAtomValue(sessionsSortOrderAtom);

  // Urgency filter is server-side now. Gate on the feature flag so a stale
  // selection can't filter when the feature is off (mirrors the SessionsListPanel).
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();
  const rawIsUrgentOnly = useAtomValue(isUrgentOnlyFilterAtom);
  const isUrgentOnly = isSessionUrgencyEnabled && rawIsUrgentOnly;

  // Convert date range to ISO strings for query key and API calls
  const dateFrom = selectedDateRange.from?.toISOString();
  const dateTo = selectedDateRange.to?.toISOString();

  // Build filter object for query key (ensures cache invalidation on filter changes)
  const filters = useMemo(
    () => ({
      q: searchValue || undefined, // Empty string should be undefined
      sessionStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      dateFrom,
      dateTo,
      patientProfileIds:
        selectedPatientProfiles.length > 0 ? selectedPatientProfiles : undefined,
      sortBy: sortByMapping[sortBy] ?? 'created_at',
      sortOrder: mapSortOrderToBackend(sortOrder),
      untitledText,
      isUrgentOnly,
    }),
    [
      searchValue,
      selectedStatuses,
      dateFrom,
      dateTo,
      selectedPatientProfiles,
      sortBy,
      sortOrder,
      untitledText,
      isUrgentOnly,
    ]
  );

  return useInfiniteQuery({
    queryKey: [GET_INFINITE_SESSIONS_WITH_PAGINATION, pageSize, filters],
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    staleTime: 0, // set to 0 to ensure we refetch on window focus
    retry: SESSIONS_QUERY_RETRY,
    retryDelay: SESSIONS_QUERY_RETRY_DELAY,
    enabled: Boolean(
      isAuthenticated && !!user && isDesktopOnlineReady && sessionQueryVersion === 'v3'
    ),
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      // pageParam === 1 is the initial list load; any higher page is a
      // distinct "next page" fetch (infinite scroll).
      const isNextPage = pageParam > 1;
      try {
        const { data } = await getSessionsWithPagination({
          q: filters.q,
          pageSize,
          sessionStatus: filters.sessionStatus,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
          patientProfileIds: filters.patientProfileIds,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          untitledText: filters.untitledText,
          includeTotalCount: true,
          page: pageParam,
          isUrgent: filters.isUrgentOnly ? true : undefined,
        });

        logger.info(
          isNextPage
            ? '[SessionList] Loaded next session page successfully'
            : '[SessionList] Loaded sessions successfully'
        );
        return data;
      } catch (error) {
        logger.error(
          isNextPage
            ? '[SessionList] Failed to load next session page'
            : '[SessionList] Failed to load sessions',
          sanitizeError(error)
        );
        throw error;
      }
    },
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!lastPage.has_next_page) {
        return undefined;
      }
      return lastPageParam + 1;
    },
    initialPageParam: 1,
  });
}

// Cache identities are single-site in `@heidi/session/contracts` (ARCFE-1001).
// These were DOUBLE-DECLARED here — the literals agreed, but two independent
// declarations of one cache key is an unguarded drift risk: a change to either
// side splits writes and reads across two caches. Imported (not just
// re-exported) because this module uses them locally too.
export { GET_INFINITE_SESSIONS_WITH_PAGINATION };
